import { Box, Card, CardContent, Paper, Typography, Divider } from '@mui/material';
import Grid from '@mui/material/Grid';
import type { EntityDetailContext, EntityDetailRenderer, EntityDetailTab } from '../types';
import { JsonFormsRenderer } from '@/components/JsonFormsRenderer';

/**
 * Generic entity detail renderer that works for any entity type.
 * Uses JSON Forms for dynamic schema-based rendering.
 */
export const createGenericDetailRenderer = (): EntityDetailRenderer => {
  return {
    renderTabs: (context: EntityDetailContext): EntityDetailTab[] => {
      const { entity, entityConfig } = context;

      const formatValue = (value: unknown): string => {
        if (value === null || value === undefined) {
          return 'N/A';
        }
        if (typeof value === 'object') {
          return JSON.stringify(value, null, 2);
        }
        if (typeof value === 'boolean') {
          return value ? 'Yes' : 'No';
        }
        return String(value);
      };

      const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return 'Invalid Date';
        }
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
      };

      return [
        {
          label: 'Entity Details',
          content: (
            <Box>
              <Card>
                <CardContent>
                  {entityConfig?.entity_schema ? (
                    <JsonFormsRenderer
                      schema={entityConfig.entity_schema}
                      uiSchema={entityConfig.ui_schema}
                      data={{
                        ...entity.entity_data,
                        entity_identifier: entity.entity_identifier,
                        status: entity.status,
                      }}
                      readonly={true}
                    />
                  ) : (
                    <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.100' }}>
                      <pre
                        style={{
                          margin: 0,
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {formatValue(entity.entity_data)}
                      </pre>
                    </Paper>
                  )}
                </CardContent>
              </Card>
            </Box>
          ),
        },
        {
          label: 'System Information',
          content: (
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      eQuip System Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Entity ID:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {entity.id}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Status:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography variant="body2">{entity.status}</Typography>
                      </Grid>

                      <Grid size={{ xs: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Version:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography variant="body2">{entity.version}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Timestamps */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      eQuip Asset Management System
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Created in eQuip:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography variant="body2">{formatDate(entity.created_at)}</Typography>
                      </Grid>

                      <Grid size={{ xs: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Updated in eQuip:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography variant="body2">{formatDate(entity.updated_at)}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ),
        },
      ];
    },
  };
};
